import { refundsTableFieldsFragment } from "@lyfegen-platform/graphql-schema/fragments";
import gql from "graphql-tag";
import {
	preparationFieldsFragment,
	scenarioFieldsFragment,
	treatmentFieldsFragment,
	treatmentSetFieldsFragment,
} from "../../../controllers/queries";

export const GET_REFUNDS_FILTERS = gql`
	query GetRefundsFilters {
		GetRefundsTableSourceFilterData {
			tags
			purchaseOrPatientIds
			recordTypes
			counterparties
			products
			statuses
			reviewersOrganizations
			amountRanges {
				start
				end
				count
			}
			rateRanges {
				start
				end
				count
			}
			quantityRanges {
				start
				end
				count
			}
			basePricesRanges {
				start
				end
				count
			}
			minInvoiceDate
			maxInvoiceDate
			minAdministrationDate
			maxAdministrationDate
			minAdjustedAt
			maxAdjustedAt
			legalEntities
			caseOrAgreementIds
			ids
			documentIds
			predecessorIds
			successorIds
			isDeltaValues
		}
		filters: GetUserContextFiltersRefunds
		GetOrganizationCaselessAgreementStatus {
			hasOnlyCaselessAgreements
			hasOnlyPatientAgreements
		}
	}
`;
export const UPDATE_REFUNDS_FILTER = gql`
	mutation UpdateUserContextFiltersRefunds($req: String) {
		UpdateUserContextFiltersRefunds(req: $req)
	}
`;

export const GET_ALL_REFUND_IDS = gql`
	query GetAllRefundIds($where: RefundTableRowWhere!, $options: RefundTableRowOptions) {
		refundTableRows(where: $where, options: $options) {
			id
			status
			smartMipStatus
			quantity
			currency
			amount
			finalAmount
			canUserModifyFinancials
			documentIsConfirmedByBuyer
			documentIsConfirmedBySeller
			nonObserverTenantIds
			creatableDocument
			documentStatus
			isAgreementBuyer
			isAgreementSeller
			tenantIds
			canBeReplacedInDocumentIds
			canBeReplacementInDocumentIds
			successorIds
			removedFromDocumentId
			finalAmount
		}
	}
`;

export const GET_REFUNDS = gql`
	${refundsTableFieldsFragment}

	query GetRefunds($where: RefundTableRowWhere, $options: RefundTableRowOptions) {
		refundTableRows(where: $where, options: $options) {
			...GetRefundsFields
			finalAmount
			finalRefundablePercentage
		}

		allRefundCount: refundTableRows {
			_count
		}

		filteredRefundCount: refundTableRows(where: $where) {
			_count
		}
	}
`;

export const GET_ELIGIBLE_DOCUMENTS_TO_ADD_REFUNDS = gql`
	query GetEligibleDocumentsToAddRefunds {
		documents(where: { status_IN: [WORKING_DRAFT, IN_REVIEW], type_IN: [INVOICE, CREDIT_NOTE] }) {
			id
			type
			tenantIds
			isConfirmedByBuyer
			isConfirmedBySeller
			isConfirmedByExternalParty
			isSentToSmartMip
			confirmedBy {
				id
				tenantId
			}
		}
	}
`;

export const GET_FILTERED_REFUND_COUNT = gql`
	query GetFilteredRefundCount($where: RefundTableRowWhere) {
		filteredRefundCount: refundTableRows(where: $where) {
			_count
		}
	}
`;

export const GET_PREPARATIONS_FOR_REFUND = gql`
	query GetPreparationsForRefund($refundId: ID!) {
		refunds(where: { id: $refundId, isDeleted: false }) {
			documentStatus
			sourceFinancialDataPoint(where: { isDeleted: false }) {
				uuid
				prescriptionDate
				treatment {
					Name
					regulatedBy {
						BrandName
						DescriptionDe
					}
				}
				quantity
				basePrice
				refundablePercentage
				refundable
				currencyCode
			}
			case(where: { isDeleted: false }) {
				actualAgreementId
				sourceTenantId
				baseAgreement {
					currency {
						Code
						Name
					}
				}
			}
		}
	}
`;

const refundSourceConditionsFragment = gql`
	${treatmentFieldsFragment}
	${preparationFieldsFragment}
	${scenarioFieldsFragment}
	${treatmentSetFieldsFragment}
	fragment RefundSourceConditions on BaseAgreement {
		canUserViewFinancials
		coveringFormWaitInterval
		currency {
			Code
			Name
		}
		enrollingConditionSets {
			scopesConnection {
				edges {
					isExtended
					excludedOn
					node {
						code
						logicalOperator
						treatmentSets {
							...TreatmentSetFieldsFragment
						}
						diseases {
							Icd10Code
							Name
						}
						indications {
							id
							code
							type
							intent
							line
							mutation
							typeLocalizationKey
							intentLocalizationKey
							lineLocalizationKey
							mutationLocalizationKey
						}
						isDeleted
					}
				}
			}
		}
		allowedRecordFlexEntityTypes {
			name
			localizationKey
		}
		requiredRecordFlexEntityTypes {
			name
			localizationKey
		}
		allowedFormFlexEntityTypes {
			name
			localizationKey
		}
		requiredForms
		isCoveringIntervalEnabled
		isAllowedFormsEnabled
		isRequiredFormsEnabled
		scenarios(options: { sort: { order: ASC } }) {
			...ScenarioFields
		}
		id
		dataContainers {
			timeframeStartingPoint
			type
		}
		eligibilityConditionSetsConnection {
			edges {
				value
				minimumPercentage
				days
				previousTestNumber
				referenceTestRangeDays
				referenceAdministrationSequenceNumber
				numberOfAdministeredRangeDays
				numberOfAdministeredTreatmentsOperator
				numberOfAdministeredTreatments
				cycleAdherence
				node {
					code
					name
					inputLabel
				}
			}
		}
		eligibilityConditionTreatments {
			...TreatmentFields
		}
	}
`;

export const GET_REFUND_SOURCE_CONDITIONS = gql`
	${refundSourceConditionsFragment}

	query GetRefundSourceConditions($agreementId: String!, $caseId: ID!) {
		baseAgreements(where: { initialVersionId: $agreementId }) {
			...RefundSourceConditions
		}

		cases(where: { id: $caseId }) {
			rating
			disease {
				Icd10Code
				Name
			}
			indication {
				id
				type
				intent
				line
				mutation
				typeLocalizationKey
				intentLocalizationKey
				lineLocalizationKey
				mutationLocalizationKey
			}
		}
	}
`;

export const GET_CASELESS_REFUND_SOURCE_CONDITIONS = gql`
	${refundSourceConditionsFragment}

	query GetCaselessRefundSourceConditions($agreementId: String!) {
		baseAgreements(where: { initialVersionId: $agreementId }) {
			...RefundSourceConditions
		}
	}
`;
export const REFUNDS_SUBSCRIPTION = gql`
	subscription OnRefundCreated {
		refundCreated
	}
`;
